import { hasQuerySecret } from "@sukha/shared";

/**
 * Pages Session Replay never records, because their URL carries a credential
 * (TX-5459): `/accept-token?token=` (a Clerk sign-in token) and
 * `/connect-desktop?code=` (a device-link code).
 *
 * `beforeAddRecordingEvent` and an event processor scrub everything else a
 * replay carries, but the DOM recorder's own snapshot event holds
 * `window.location.href` and reaches no Sentry hook. Recording starts at init,
 * before the page can take the credential out of its URL, so the only way to
 * keep it out of a recording is not to record the page.
 *
 * `replayExcludedPaths.guard.test.ts` fails when a page reads a credential from
 * its query and is missing here.
 */
export const REPLAY_EXCLUDED_PATHS = ["/accept-token", "/connect-desktop"] as const;

/**
 * False on an excluded page or anything under it, and on any page whose query
 * carries a credential in one of its values — a return path such as
 * /landing?deviceLinkReturn=%2Fconnect-desktop%3Fcode%3D… (`proxyDecision`).
 * Each value is decoded once by `URLSearchParams`; `hasQuerySecret` also reads
 * what is still encoded.
 */
export function shouldRecordReplay(pathname: string, search: string): boolean {
  if (REPLAY_EXCLUDED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))) return false;
  return ![...new URLSearchParams(search).values()].some(hasQuerySecret);
}
